import React, { useState, useRef } from "react";

import { Button, ButtonGroup, MenuItem, Menu, Typography } from "@mui/material";

import { SpaceMap } from "types/SpaceMap";
import { MapStyle } from "types/MapStyle";

import styles from "components/Toggle/Toggle.module.css";
import { ArrowDropDown } from "@mui/icons-material";

type Props = {
	label: string;
	isActive: boolean;
	spaces: SpaceMap;
	onChange: (value: string) => void;
	icon?: string;
	showOptions?: boolean;
	currentValue?: MapStyle | string;
	options?: { value: string; label: string; icon?: string }[];
};

const Toggle: React.FC<Props> = ({
	label,
	isActive,
	spaces,
	currentValue,
	onChange,
	icon,
	showOptions = false,
	options,
}) => {
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const buttonRef = useRef<HTMLButtonElement>(null);

	const handleClick = () => {
		setIsDropdownOpen((prev) => !prev);
	};

	const handleSelection = (value: string) => {
		onChange(value);
		setIsDropdownOpen(false);
	};

	return (
		<>
			<ButtonGroup>
				<Button
					onClick={handleClick}
					aria-pressed={isActive}
					ref={buttonRef}
					variant='contained'
					size='small'
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 2,
					}}
				>
					{icon && <span className={styles.icon}>{icon}</span>}

					<Typography variant='body2' sx={{ whiteSpace: "nowrap" }}>
						{label}
					</Typography>

					<ArrowDropDown
						className={`${styles.arrow} ${isDropdownOpen ? styles.up : ""}`}
					/>
				</Button>
			</ButtonGroup>

			{isDropdownOpen && (
				<Menu
					open={isDropdownOpen}
					anchorEl={buttonRef.current}
					onClose={() => setIsDropdownOpen(false)}
					anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
					transformOrigin={{ vertical: "top", horizontal: "left" }}
				>
					{showOptions &&
						options?.map((option) => (
							<MenuItem
								key={option.value}
								selected={currentValue === option.value}
								onClick={() => onChange(option.value)}
								className={currentValue === option.value ? styles.active : ""}
							>
								{option.icon} {option.label}
							</MenuItem>
						))}

					{!showOptions &&
						Object.entries(spaces).map(([id, space]) => (
							<MenuItem key={id} onClick={() => handleSelection(id)}>
								{space.icon} {space.label}
							</MenuItem>
						))}
				</Menu>
			)}
		</>
	);
};

export default Toggle;
